<template>
  <section class="content-panel">
    <div class="section-head">
      <div>
        <h2>风险列表</h2>
        <p>维护阿里云设备风险标签，供风控配置选择忽略项</p>
      </div>
      <div class="head-actions">
        <el-select
          v-model="tagType"
          placeholder="标签类型"
          clearable
          style="width: 160px"
          @change="search"
        >
          <el-option v-for="t in types" :key="t" :label="t" :value="t" />
        </el-select>
        <el-input
          v-model.trim="keyword"
          placeholder="搜索标识 / 含义"
          clearable
          class="search-input"
          :prefix-icon="Search"
          @keyup.enter="search"
          @clear="search"
        />
        <el-button type="primary" @click="search">查询</el-button>
        <el-button type="primary" @click="openCreate">
          <el-icon><Plus /></el-icon>新增标签
        </el-button>
      </div>
    </div>

    <el-table :data="rows" stripe v-loading="loading">
      <el-table-column type="index" label="#" width="56" />
      <el-table-column prop="tagState" label="标签标识" min-width="180" show-overflow-tooltip />
      <el-table-column prop="tagMean" label="含义" min-width="180" show-overflow-tooltip />
      <el-table-column prop="tagType" label="类型" width="130" />
      <el-table-column prop="tagDesc" label="说明" min-width="220" show-overflow-tooltip />
      <el-table-column label="默认忽略" width="100" align="center">
        <template #default="{ row }">
          <el-switch
            :model-value="row.isDefault === 1"
            @change="(val) => toggleDefault(row, val)"
          />
        </template>
      </el-table-column>
      <el-table-column label="更新时间" width="180">
        <template #default="{ row }">{{ formatTime(row.updateTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="140" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openEdit(row)">编辑</el-button>
          <el-button link type="danger" @click="remove(row)">删除</el-button>
        </template>
      </el-table-column>
      <template #empty><el-empty description="暂无风险标签" /></template>
    </el-table>

    <div class="panel-footer">
      <el-pagination
        :current-page="pagination.page"
        :page-size="pagination.size"
        :page-sizes="[10, 20, 50]"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next"
        background
        @current-change="onPageChange"
        @size-change="onSizeChange"
      />
    </div>

    <el-dialog v-model="dialog" :title="editingId ? '编辑风险标签' : '新增风险标签'" width="640px">
      <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
        <el-form-item label="标签标识" prop="tagState">
          <el-input v-model.trim="form.tagState" placeholder="如：is_emulator" :disabled="!!editingId" />
        </el-form-item>
        <el-form-item label="标签含义" prop="tagMean">
          <el-input v-model.trim="form.tagMean" placeholder="如：设备疑似模拟器" />
        </el-form-item>
        <el-form-item label="标签类型" prop="tagType">
          <el-select
            v-model="form.tagType"
            filterable
            allow-create
            default-first-option
            placeholder="选择或输入类型"
            style="width: 100%"
          >
            <el-option v-for="t in types" :key="t" :label="t" :value="t" />
          </el-select>
        </el-form-item>
        <el-form-item label="详细说明">
          <el-input v-model.trim="form.tagDesc" type="textarea" :rows="4" placeholder="标签详细描述" />
        </el-form-item>
        <el-form-item label="新建配置时默认忽略">
          <el-switch v-model="form.isDefault" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialog = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">{{ editingId ? '保存' : '创建' }}</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { Plus, Search } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'

const props = defineProps({ admin: Object })

const loading = ref(false)
const saving = ref(false)
const dialog = ref(false)
const editingId = ref(null)
const formRef = ref()
const keyword = ref('')
const tagType = ref('')
const types = ref([])
const rows = ref([])
const pagination = reactive({ page: 1, size: 20, total: 0 })

const empty = () => ({
  tagState: '',
  tagMean: '',
  tagType: '设备风险类',
  tagDesc: '',
  isDefault: 0
})
const form = reactive(empty())

const rules = {
  tagState: [{ required: true, message: '请输入标签标识' }],
  tagMean: [{ required: true, message: '请输入标签含义' }],
  tagType: [{ required: true, message: '请选择或输入标签类型' }]
}

function formatTime(ms) {
  if (!ms) return '—'
  const d = new Date(Number(ms))
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('zh-CN', { hour12: false })
}

async function loadTypes() {
  try {
    types.value = (await props.admin.call('/api/admin/risk-tags/types')) || []
  } catch {
    types.value = []
  }
}

async function loadList() {
  loading.value = true
  try {
    const params = new URLSearchParams({
      page: String(pagination.page),
      size: String(pagination.size)
    })
    if (keyword.value) params.set('keyword', keyword.value)
    if (tagType.value) params.set('tagType', tagType.value)
    const data = await props.admin.call(`/api/admin/risk-tags/list?${params}`)
    rows.value = data?.records || []
    pagination.total = Number(data?.total || 0)
  } catch (error) {
    ElMessage.error(error.message)
  } finally {
    loading.value = false
  }
}

async function search() {
  pagination.page = 1
  await loadList()
}

async function onPageChange(page) {
  pagination.page = page
  await loadList()
}

async function onSizeChange(size) {
  pagination.size = size
  pagination.page = 1
  await loadList()
}

function openCreate() {
  editingId.value = null
  Object.assign(form, empty())
  dialog.value = true
}

function openEdit(row) {
  editingId.value = row.id
  Object.assign(form, {
    tagState: row.tagState || '',
    tagMean: row.tagMean || '',
    tagType: row.tagType || '',
    tagDesc: row.tagDesc || '',
    isDefault: row.isDefault === 1 ? 1 : 0
  })
  dialog.value = true
}

async function submit() {
  if (!await formRef.value.validate().catch(() => false)) return
  saving.value = true
  try {
    const payload = {
      tagState: form.tagState,
      tagMean: form.tagMean,
      tagType: form.tagType,
      tagDesc: form.tagDesc,
      isDefault: form.isDefault === 1 ? 1 : 0
    }
    if (editingId.value) {
      await props.admin.call(`/api/admin/risk-tags/${editingId.value}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      })
      ElMessage.success('标签已更新')
    } else {
      await props.admin.call('/api/admin/risk-tags/create', {
        method: 'POST',
        body: JSON.stringify(payload)
      })
      ElMessage.success('标签已创建')
    }
    dialog.value = false
    await loadTypes()
    await loadList()
  } catch (error) {
    ElMessage.error(error.message)
  } finally {
    saving.value = false
  }
}

async function remove(row) {
  try {
    await ElMessageBox.confirm(`确认删除标签「${row.tagMean || row.tagState}」？`, '删除确认', { type: 'warning' })
  } catch {
    return
  }
  try {
    await props.admin.call(`/api/admin/risk-tags/${row.id}`, { method: 'DELETE' })
    ElMessage.success('已删除')
    await loadTypes()
    await loadList()
  } catch (error) {
    ElMessage.error(error.message)
  }
}

async function toggleDefault(row, val) {
  const isDefault = val ? 1 : 0
  try {
    await props.admin.call(`/api/admin/risk-tags/${row.id}/default?isDefault=${isDefault}`, { method: 'PUT' })
    row.isDefault = isDefault
    ElMessage.success(isDefault ? '已设为新建配置默认忽略' : '已取消默认忽略')
  } catch (error) {
    ElMessage.error(error.message)
    await loadList()
  }
}

onMounted(async () => {
  await loadTypes()
  await loadList()
})
</script>
